import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send, CheckCircle2 } from 'lucide-react';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';

const Contato = () => {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ nome: '', empresa: '', email: '', telefone: '', mensagem: '' });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) {
      toast({ title: 'Preencha nome e e-mail', description: 'Precisamos desses dados para retornar seu contato.' });
      return;
    }
    setSent(true);
    toast({ title: 'Mensagem enviada!', description: 'Nosso time entrará em contato em até 24h úteis.' });
  };

  return (
    <>
      <Seo
        title="Contato"
        description="Fale com o time da CoreDesk pelo WhatsApp, e-mail ou telefone. Tire dúvidas e descubra como vender e atender em escala com agentes de IA."
        path="/contato"
      />
      <Header />
      <PageHero
        eyebrow="Fale com a gente"
        title="Vamos conversar |sobre o seu negócio"
        subtitle="Nosso time está pronto para entender seu cenário e mostrar como a CoreDesk pode ajudar. Respondemos em até 24h úteis."
      />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10">
            {/* Form */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-lg p-8 md:p-10">
              {sent ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 size={30} className="text-emerald-600" />
                  </div>
                  <h3 className="mt-5 text-2xl font-extrabold text-[#0a0e27]">Mensagem enviada!</h3>
                  <p className="mt-2 text-slate-600">Nosso time vai retornar em até 24 horas úteis.</p>
                  <Button onClick={() => { setSent(false); setForm({ nome: '', empresa: '', email: '', telefone: '', mensagem: '' }); }} variant="outline" className="mt-6 rounded-full">
                    Enviar outra mensagem
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-extrabold text-[#0a0e27]">Envie sua mensagem</h3>
                    <p className="text-sm text-slate-500 mt-1">Preencha e nosso time entra em contato.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Nome*</label>
                      <Input name="nome" value={form.nome} onChange={onChange} placeholder="Seu nome" className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Empresa</label>
                      <Input name="empresa" value={form.empresa} onChange={onChange} placeholder="Nome da empresa" className="mt-1.5 h-11" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600">E-mail*</label>
                      <Input name="email" type="email" value={form.email} onChange={onChange} placeholder="voce@empresa.com" className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600">Telefone</label>
                      <Input name="telefone" value={form.telefone} onChange={onChange} placeholder="(11) 90000-0000" className="mt-1.5 h-11" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Como podemos ajudar?</label>
                    <Textarea name="mensagem" value={form.mensagem} onChange={onChange} placeholder="Conte um pouco sobre seu negócio..." rows={5} className="mt-1.5" />
                  </div>
                  <Button type="submit" className="btn-coral w-full h-12 rounded-full font-semibold gap-2">
                    Enviar mensagem <Send size={15} />
                  </Button>
                  <p className="text-[11px] text-slate-400 text-center">
                    Ao enviar, você concorda com nossa Política de Privacidade e o tratamento de dados conforme a LGPD.
                  </p>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="space-y-4">
              <a href="https://wa.me/5511974512072?text=Ol%C3%A1!%20Quero%20falar%20com%20a%20CoreDesk." target="_blank" rel="noopener noreferrer" className="block bg-[#25D366] text-white rounded-2xl p-6 hover:shadow-2xl transition-shadow">
                <MessageCircle size={22} />
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-white/80">Mais rápido</div>
                <div className="mt-1 text-xl font-extrabold">Fale no WhatsApp</div>
                <div className="mt-1 text-sm text-white/90">Resposta em minutos, horário comercial.</div>
              </a>

              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <Mail size={20} className="text-[#FF5B24]" />
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">E-mail</div>
                <a href="mailto:contato@coredesk.com.br" className="mt-1 block text-lg font-extrabold text-[#0a0e27] hover:text-[#FF5B24]">contato@coredesk.com.br</a>
                <div className="mt-1 text-sm text-slate-500">Resposta em até 24h úteis.</div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <Phone size={20} className="text-[#FF5B24]" />
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Comercial</div>
                <a href="tel:+5511974512072" className="mt-1 block text-lg font-extrabold text-[#0a0e27] hover:text-[#FF5B24]">+55 11 97451-2072</a>
                <div className="mt-1 text-sm text-slate-500">Seg. a Sex., 9h às 18h.</div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <MapPin size={20} className="text-[#FF5B24]" />
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Endereço</div>
                <div className="mt-1 text-sm text-[#0a0e27] font-medium leading-relaxed">Rua Áureliano Guimarães, 172<br/>São Paulo — SP</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contato;
